import { HttpErrorResponse } from '@angular/common/http';
import { EnrollRequest, EnrollResponse } from '../../core/models/enrollment.model';
import { EnrollmentService } from '../../core/services/enrollment.service';
import { Component } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LanguageEnum, MerchantTypeEnum } from '@rezolved/mxp-proto';
import { BaseDropDownItem, BaseDropDownItemUtils } from '@rezolved/ui';
import { NumberPatternEnum } from 'libs/ui/src/lib/enums/number-pattern.enum';
import { MxpSnackBarService } from '../../core/services/mxp-snackbar.service';
import { Router } from '@angular/router';
import Route from '../../core/constants/Route';
import { MatDialog } from '@angular/material/dialog';
import { TermsConditionsComponent } from './terms-conditions/terms-conditions.component';
import { AuthOErrorCodeEnum, BanxicoErrorCodeEnum } from '../../core/enums/enrollment-error-code.enum';

@Component({
  selector: 'app-merchant-enrollment',
  templateUrl: './merchant-enrollment.component.html',
  styleUrls: ['./merchant-enrollment.component.scss']
})
export class MerchantEnrollmentComponent {

  isLoading = false;
  isProduction: boolean = false;
  isSigned = false;

  form: FormGroup;
  langForm: FormGroup;

  languages: BaseDropDownItem[] = [];
  pattern = NumberPatternEnum.POSITIVE_DECIMAL;
  //termsAndConditionsAgreed = true; //TODO: change to false when terms and conditions are back
  
  byteArraySignature: Uint8Array = new Uint8Array();
  merchantType: MerchantTypeEnum = MerchantTypeEnum.PREPAYMENT;
  
  constructor(
    private mxpSnackBarService: MxpSnackBarService,
    private translate: TranslateService,
    private enrollmentService: EnrollmentService,
    private router: Router,
    private tncDialog: MatDialog
  ) {
    this.isProduction = window.location.href.includes('https://mxp.mx.rezolve.com/');
    this.languages = BaseDropDownItemUtils.transformEnum(LanguageEnum);
    this.langForm = new FormGroup({
      lang: new FormControl(this.languages.find((x) => x.key === this.translate.getDefaultLang().toUpperCase())?.value)
    });
    this.form = new FormGroup(    {
      merchantName: new FormControl('', Validators.compose([Validators.required, Validators.maxLength(100), Validators.pattern(this.isProduction ? "^[a-zA-Z0-9, ]*$" : "^[a-zA-Z0-9, #]*$")])),
      merchantRfc: new FormControl('', Validators.maxLength(20)),
      merchantBankAccount: new FormControl('',  Validators.compose([Validators.required, Validators.maxLength(20)])),
      userFirstname: new FormControl('', Validators.required),
      userSurnameFirst: new FormControl('', Validators.required),
      userSurnameSecond: new FormControl('', Validators.required),
      userEmail: new FormControl('', Validators.compose([Validators.required, Validators.email]))
    });

    this.langForm.get('lang')?.valueChanges.subscribe((value) => {
      if (value === LanguageEnum.EN) {
        this.translate.setDefaultLang('en');
      } else {
        this.translate.setDefaultLang('es');
      }
    });

  }

  onSubmit() {
    this.isLoading = true;
    const {
      merchantName,
      merchantRfc,
      merchantBankAccount,
      userEmail,
      //termsAndConditions,
      userFirstname,
      userSurnameFirst,
      userSurnameSecond
      } = this.form.value;

    const request: EnrollRequest = {
      merchantName, merchantRfc, merchantBankAccount, userEmail,
      userFirstname, userSurnameFirst, userSurnameSecond,
      userSignature: btoa(Array.from(this.byteArraySignature, byte => String.fromCharCode(byte)).join('')),
      merchantType: this.merchantType
    };

    this.enrollmentService.enroll(request).subscribe(
      (response: EnrollResponse) => {
        this.isLoading = false;
        if (response?.userId) {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT');
          this.router.navigate([Route.MAIN_PAGE]);
        } else {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT_FAIL');
        }
      },
      (error: HttpErrorResponse) => {
        console.log(error);
        this.isLoading = false;
        const errorCode = Number(error.error?.errorCode);
        const specificErrorCode = this.getHttpMetadata(error, 'banxicoerrorcode') ?? this.getHttpMetadata(error, 'auth0errorcode');
        const operationType = this.getHttpMetadata(error, 'banxicooperationtype') ?? this.getHttpMetadata(error, 'auth0operationtype');

        if (errorCode == EnrollmentErrorCode.BanxicoEnrollmentError && Number(specificErrorCode) == BanxicoErrorCodeEnum.ALREADY_REGISTERED)
        {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT_ERROR_ACCOUNT_ALREADY_REGISTERED');
        }
        else if (errorCode == EnrollmentErrorCode.BanxicoEnrollmentError && Number(specificErrorCode) == BanxicoErrorCodeEnum.WRONG_RFC)
        {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT_ERROR_WRONG_RFC');
        }
        else if (errorCode == EnrollmentErrorCode.Auth0OperationError && operationType == 'AUTH0_REGISTRATION' && specificErrorCode == AuthOErrorCodeEnum.AUTH0_IDP_ERROR)
        {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT_ERROR_EMAIL_ALREADY_REGISTERED');
        }
        else
        {
          this.mxpSnackBarService.showSnackbarMessage('SNACKBAR.ENROLL_MERCHANT_FAIL');
        }
      },
    );
  }

  handleIsEmptySignature(isSignatureEmpty: boolean) {
    this.isSigned = !isSignatureEmpty;
  }

  handleOnSaveSignature(signatureByteArray: Uint8Array) {
    if (signatureByteArray != undefined) {
      this.byteArraySignature = signatureByteArray;
      this.isSigned = true;
      return;
    }
    this.isSigned = false;
  }

  // handleAgreement() {
  //   this.termsAndConditionsAgreed = !this.termsAndConditionsAgreed;
  // }

  //openTandCPopUp() {
  //  this.tncDialog.open(TermsConditionsComponent, { data: { message: 'T_AND_C_WRITEUP' } });
  //}

  private getHttpMetadata(error: HttpErrorResponse, key: string): string | undefined {
    const metadata: Record<string, string> = error.error?.metadata || {};
    const matchingKey = Object.keys(metadata).find(name => name.toLowerCase() === key.toLowerCase());
    return matchingKey ? metadata[matchingKey] : undefined;
  }
}

enum EnrollmentErrorCode {
  BanxicoEnrollmentError = 2000,
  Auth0OperationError = 3000
}
